
import Navbar from '../visa-travel-tips/components/Navbar';
import Footer from '../visa-travel-tips/components/Footer';

export default function PublicTransportGuide() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 to-teal-600 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Hướng dẫn sử dụng phương tiện công cộng tại Mỹ
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Master public transit từ NYC Subway đến SF Muni - tiết kiệm và di chuyển như người địa phương
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <a href="/" className="hover:text-teal-600 transition-colors">Trang chủ</a>
            <span>/</span>
            <a href="/visa-travel-tips" className="hover:text-teal-600 transition-colors">Kinh nghiệm du lịch</a>
            <span>/</span>
            <span className="text-gray-900 font-medium">Phương tiện công cộng</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-8 mb-12">
            <p className="text-lg leading-relaxed text-gray-700 mb-0">
              Public transit tại Mỹ rất đa dạng - từ hệ thống subway phức tạp của NYC đến light rail hiện đại ở Seattle. 
              Mỗi thành phố có system riêng với rules, pricing và culture khác nhau. 
              Master được public transit sẽ giúp bạn tiết kiệm đáng kể và trải nghiệm như người địa phương.
            </p>
          </div>

          {/* Major City Systems */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">🚇 Major City Transit Systems</h2>

            {/* NYC Subway */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-subway-line text-blue-600 text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🗽 New York City Subway (MTA)</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        Largest transit system in North America với 472 stations, 27 lines, 24/7 operation. 
                        Complex nhưng comprehensive - đi được mọi nơi trong 5 boroughs.
                      </p>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">💳 Payment & Pricing:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Single ride:</strong> $2.90 (MetroCard/OMNY)</li>
                          <li>• <strong>7-day unlimited:</strong> $33</li>
                          <li>• <strong>30-day unlimited:</strong> $127</li>
                          <li>• <strong>OMNY:</strong> Tap phone/contactless card</li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-900 mb-2">🗺️ Navigation tips:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Express vs Local:</strong> Express faster, fewer stops</li>
                          <li>• <strong>Uptown:</strong> North (Bronx direction)</li>
                          <li>• <strong>Downtown:</strong> South (Brooklyn direction)</li>
                          <li>• <strong>CrossTown:</strong> East-West lines (L, 7)</li>
                          <li>• <strong>Weekend service:</strong> Frequent changes</li>
                        </ul>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-2">⚠️ Safety & etiquette:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Stand right, walk left on escalators</li>
                          <li>• Let people exit before boarding</li>
                          <li>• Move to center of car</li>
                          <li>• Keep valuables secure</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* San Francisco Muni */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-train-line text-orange-600 text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🌉 San Francisco Muni + BART</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        Muni: City transit với buses, light rail, cable cars, streetcars. 
                        BART: Regional rail connecting SF, Oakland, Berkeley, SFO Airport.
                      </p>
                      <div className="bg-orange-50 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-900 mb-2">🚌 Muni System:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Single ride:</strong> $3 adults, $1.50 youth/senior</li>
                          <li>• <strong>Day pass:</strong> $5 (unlimited Muni)</li>
                          <li>• <strong>Cable cars:</strong> $8/ride (tourist attraction)</li>
                          <li>• <strong>Clipper Card:</strong> Reloadable, works all Bay Area transit</li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-900 mb-2">🚄 BART System:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Distance-based pricing:</strong> $2-15+ depending on zones</li>
                          <li>• <strong>SFO Airport:</strong> $10.25 from downtown SF</li>
                          <li>• <strong>Oakland Airport:</strong> $11.45 from SF</li>
                          <li>• <strong>Frequency:</strong> 4-20 minutes depending on time</li>
                        </ul>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-900 mb-2">💡 Pro tips:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Use Clipper Card for seamless transfers</li>
                          <li>• Muni Metro underground trong downtown</li>
                          <li>• BART không run 24/7 (midnight-5AM)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* DC Metro */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-government-line text-red-600 text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🏛️ Washington DC Metro (WMATA)</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        Clean, efficient system với 6 color-coded lines. Connects DC với Virginia và Maryland suburbs. 
                        Popular với tourists visiting monuments và museums.
                      </p>
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-2">💰 Pricing structure:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Peak hours:</strong> $2.25-6.75 (weekdays 5-9:30AM, 3-7PM)</li>
                          <li>• <strong>Off-peak:</strong> $2-5.75</li>
                          <li>• <strong>Weekend:</strong> Off-peak rates all day</li>
                          <li>• <strong>Day pass:</strong> $14.75 unlimited rides</li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">🎯 Tourist benefits:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Smithsonian stop:</strong> Access to museums</li>
                          <li>• <strong>National Mall:</strong> Easy access to monuments</li>
                          <li>• <strong>Reagan Airport:</strong> Blue/Yellow lines</li>
                          <li>• <strong>Clean & safe:</strong> Well-maintained system</li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-2">⏰ Operating hours:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Weekdays:</strong> 5AM-11:30PM</li>
                          <li>• <strong>Saturday:</strong> 7AM-1AM</li>
                          <li>• <strong>Sunday:</strong> 8AM-11:30PM</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chicago CTA */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-2-line text-green-600 text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🏙️ Chicago CTA (L Train)</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        "The L" - iconic elevated train system với 8 color-coded lines. 
                        Extensive network covering city và suburbs, including direct service to both airports.
                      </p>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-900 mb-2">🚇 System highlights:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Single ride:</strong> $2.50 rail, $2.25 bus</li>
                          <li>• <strong>Day pass:</strong> $20 unlimited</li>
                          <li>• <strong>Airport connections:</strong> Blue line to O'Hare ($5), Orange to Midway ($2.50)</li>
                          <li>• <strong>Ventra Card:</strong> Reloadable payment</li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-900 mb-2">🎨 Color-coded lines:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Red/Blue:</strong> 24/7 service</li>
                          <li>• <strong>Green/Orange/Brown/Purple/Pink/Yellow:</strong> Limited night service</li>
                          <li>• <strong>Loop:</strong> Downtown circular route</li>
                          <li>• <strong>Express services:</strong> Rush hour additions</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Regional & Light Rail */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">🚊 Light Rail & Regional Systems</h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-4 text-center">🌲 Seattle Sound Transit</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Light Rail (Link):</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Airport connection:</strong> $2.50-4.25 to/from SeaTac</li>
                      <li>• <strong>Downtown tunnel:</strong> Underground through city center</li>
                      <li>• <strong>ORCA Card:</strong> Regional payment system</li>
                      <li>• <strong>Expanding network:</strong> New lines opening</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Buses & Ferries:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Metro buses:</strong> Extensive city coverage</li>
                      <li>• <strong>Washington State Ferries:</strong> Scenic island connections</li>
                      <li>• <strong>RapidRide:</strong> Bus rapid transit</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-purple-900 mb-4 text-center">🌴 Los Angeles Metro</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">Rail System:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>6 lines:</strong> Red/Purple (subway), Blue/Green/Gold/Expo (light rail)</li>
                      <li>• <strong>Single ride:</strong> $1.75, day pass $7</li>
                      <li>• <strong>TAP Card:</strong> Reloadable payment</li>
                      <li>• <strong>Airport connection:</strong> Green Line + shuttle</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">Challenges:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Limited coverage:</strong> Car culture dominates</li>
                      <li>• <strong>Safety concerns:</strong> Some areas rough at night</li>
                      <li>• <strong>Expanding:</strong> Major investments ongoing</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-orange-900 mb-4 text-center">🤠 Dallas DART</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">Light Rail Network:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>4 colored lines:</strong> Red, Blue, Green, Orange</li>
                      <li>• <strong>Airport service:</strong> Orange Line to DFW ($2.50)</li>
                      <li>• <strong>Regional coverage:</strong> Dallas county suburbs</li>
                      <li>• <strong>GoPass app:</strong> Mobile ticketing</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-red-900 mb-4 text-center">🏔️ Denver RTD</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2">Commuter Rail:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>A-Line:</strong> To Denver Airport ($10.50)</li>
                      <li>• <strong>Light rail:</strong> 6 lines serving metro area</li>
                      <li>• <strong>Free Mall Ride:</strong> Downtown 16th Street</li>
                      <li>• <strong>MyRide Card:</strong> Regional payment</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Payment Methods */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">💳 Payment Methods & Cards</h2>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8 mb-8">
              <h3 className="text-xl font-bold text-green-900 mb-6 text-center">💰 Universal Payment Trends</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-6 text-center">
                  <div className="text-3xl mb-3">📱</div>
                  <h4 className="font-bold text-green-800 mb-3">Contactless Payment</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Apple Pay, Google Pay, contactless credit cards được accept rộng rãi.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li>• Faster than traditional cards</li>
                    <li>• More secure than cash</li>
                    <li>• Works on most systems</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 text-center">
                  <div className="text-3xl mb-3">🎫</div>
                  <h4 className="font-bold text-blue-800 mb-3">Regional Transit Cards</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Mỗi thành phố có card riêng với benefits và discounts.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li>• Better rates than single rides</li>
                    <li>• Automatic transfers</li>
                    <li>• Reload easily</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 text-center">
                  <div className="text-3xl mb-3">💵</div>
                  <h4 className="font-bold text-purple-800 mb-3">Cash Payment</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Still accepted nhưng often không có change, cần exact fare.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li>• Exact change required</li>
                    <li>• Less convenient</li>
                    <li>• Being phased out</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-yellow-50 rounded-lg p-6">
                <h4 className="font-semibold text-yellow-900 mb-4">🎯 City-Specific Cards</h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex justify-between">
                    <span><strong>NYC:</strong> MetroCard/OMNY</span>
                    <span className="text-green-600">✓ Phasing to OMNY</span>
                  </li>
                  <li className="flex justify-between">
                    <span><strong>SF Bay Area:</strong> Clipper Card</span>
                    <span className="text-green-600">✓ All transit modes</span>
                  </li>
                  <li className="flex justify-between">
                    <span><strong>DC:</strong> SmarTrip Card</span>
                    <span className="text-green-600">✓ Metro + buses</span>
                  </li>
                  <li className="flex justify-between">
                    <span><strong>Chicago:</strong> Ventra Card</span>
                    <span className="text-green-600">✓ CTA + Pace</span>
                  </li>
                  <li className="flex justify-between">
                    <span><strong>Seattle:</strong> ORCA Card</span>
                    <span className="text-green-600">✓ Regional system</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-4">💡 Money-saving strategies</h4>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Day/week passes:</strong> Better value for multiple rides</li>
                  <li>• <strong>Off-peak travel:</strong> Some systems have lower rates</li>
                  <li>• <strong>Transfer discounts:</strong> Reduced fare for connections</li>
                  <li>• <strong>Student/senior rates:</strong> Show valid ID</li>
                  <li>• <strong>Mobile apps:</strong> Sometimes have exclusive deals</li>
                  <li>• <strong>Tourist passes:</strong> Multi-day unlimited options</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Apps & Navigation */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">📱 Essential Apps & Navigation</h2>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-green-50 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-line text-white text-2xl"></i>
                </div>
                <h4 className="font-bold text-green-900 mb-3">Universal Navigation</h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">Google Maps</h5>
                    <p className="text-xs text-gray-600">Real-time transit, multiple routes, walking directions</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">Apple Maps</h5>
                    <p className="text-xs text-gray-600">iOS integrated, good transit in major cities</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">Citymapper</h5>
                    <p className="text-xs text-gray-600">Detailed transit info, available in select cities</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-smartphone-line text-white text-2xl"></i>
                </div>
                <h4 className="font-bold text-blue-900 mb-3">Official Transit Apps</h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">MTA (NYC)</h5>
                    <p className="text-xs text-gray-600">Real-time arrivals, service alerts, trip planner</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">SF Muni Mobile</h5>
                    <p className="text-xs text-gray-600">NextBus predictions, route maps</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">DC MetroHero</h5>
                    <p className="text-xs text-gray-600">Accurate predictions, crowd-sourced delays</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-payment-line text-white text-2xl"></i>
                </div>
                <h4 className="font-bold text-purple-900 mb-3">Payment & Ticketing</h4>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">Transit Apps</h5>
                    <p className="text-xs text-gray-600">Many cities allow mobile ticket purchase</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">Wallet Apps</h5>
                    <p className="text-xs text-gray-600">Apple/Google Pay for contactless payment</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <h5 className="font-medium mb-1">Transit Cards</h5>
                    <p className="text-xs text-gray-600">Digital versions in city apps</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6">
              <h4 className="font-semibold text-yellow-800 mb-3">📍 Navigation Pro Tips</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Download offline maps:</strong> Works without internet</li>
                  <li>• <strong>Check service alerts:</strong> Before leaving hotel</li>
                  <li>• <strong>Alternative routes:</strong> Always have backup plan</li>
                  <li>• <strong>Rush hour timing:</strong> Avoid 7-9AM, 5-7PM</li>
                </ul>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Weekend service:</strong> Often reduced/different routes</li>
                  <li>• <strong>Late night options:</strong> Limited service after midnight</li>
                  <li>• <strong>Tourist areas:</strong> May have crowded trains</li>
                  <li>• <strong>Local knowledge:</strong> Ask hotel staff for tips</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Safety & Etiquette */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">🛡️ Safety & Etiquette Guide</h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-red-900 mb-4 text-center">🚨 Safety Guidelines</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2">Personal Security:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Keep valuables in front pockets hoặc inner jacket</li>
                      <li>• Don't display expensive electronics conspicuously</li>
                      <li>• Stay alert, avoid headphones at full volume</li>
                      <li>• Trust instincts - move cars if uncomfortable</li>
                      <li>• Keep emergency cash separate from wallet</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2">Platform Safety:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Stand away from platform edge until train stops</li>
                      <li>• Let passengers exit before boarding</li>
                      <li>• Be aware of gap between train và platform</li>
                      <li>• Don't lean on doors hoặc try to hold them</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-green-900 mb-4 text-center">🤝 Transit Etiquette</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2">Boarding & Riding:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Move to center of car, don't block doors</li>
                      <li>• Offer priority seats to elderly, disabled, pregnant</li>
                      <li>• Keep backpack in front during rush hour</li>
                      <li>• Use headphones, keep music/calls quiet</li>
                      <li>• Don't eat messy food hoặc drink alcohol</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2">Escalators & Stairs:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Stand right, walk left (universal rule)</li>
                      <li>• Hold handrail, face forward</li>
                      <li>• Don't stop at bottom/top - keep moving</li>
                      <li>• Large luggage: use elevator when available</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-blue-800 mb-4 text-center">🆘 Emergency Procedures</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl mb-2">📞</div>
                  <h5 className="font-medium mb-2">Emergency Contacts</h5>
                  <p className="text-sm text-gray-600">911 for emergencies, transit police numbers in each city</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-2">🚪</div>
                  <h5 className="font-medium mb-2">Evacuation</h5>
                  <p className="text-sm text-gray-600">Follow crew instructions, know emergency exit locations</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-2">🏥</div>
                  <h5 className="font-medium mb-2">Medical Emergency</h5>
                  <p className="text-sm text-gray-600">Alert train operator, call 911, provide first aid if trained</p>
                </div>
              </div>
            </div>
          </section>

          {/* Budget Comparison */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">💰 Cost Comparison: Transit vs Other Options</h2>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-green-900 mb-6 text-center">🚗 Daily Transportation Costs</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white border-b-2 border-gray-300">
                      <th className="text-left p-4 font-bold">Transportation Mode</th>
                      <th className="text-left p-4 font-bold">NYC</th>
                      <th className="text-left p-4 font-bold">SF</th>
                      <th className="text-left p-4 font-bold">DC</th>
                      <th className="text-left p-4 font-bold">Chicago</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-green-100 border-b">
                      <td className="p-4 font-medium">Public Transit (Day Pass)</td>
                      <td className="p-4 text-green-700 font-bold">$33 (7-day)</td>
                      <td className="p-4 text-green-700 font-bold">$25</td>
                      <td className="p-4 text-green-700 font-bold">$15</td>
                      <td className="p-4 text-green-700 font-bold">$20</td>
                    </tr>
                    <tr className="bg-yellow-100 border-b">
                      <td className="p-4 font-medium">Uber/Lyft (4 rides)</td>
                      <td className="p-4 text-yellow-700">$60-100</td>
                      <td className="p-4 text-yellow-700">$50-80</td>
                      <td className="p-4 text-yellow-700">$40-70</td>
                      <td className="p-4 text-yellow-700">$35-60</td>
                    </tr>
                    <tr className="bg-orange-100 border-b">
                      <td className="p-4 font-medium">Taxi (4 rides)</td>
                      <td className="p-4 text-orange-700">$80-120</td>
                      <td className="p-4 text-orange-700">$60-100</td>
                      <td className="p-4 text-orange-700">$50-80</td>
                      <td className="p-4 text-orange-700">$45-75</td>
                    </tr>
                    <tr className="bg-red-100">
                      <td className="p-4 font-medium">Car Rental + Parking</td>
                      <td className="p-4 text-red-700">$100-200</td>
                      <td className="p-4 text-red-700">$80-150</td>
                      <td className="p-4 text-red-700">$60-120</td>
                      <td className="p-4 text-red-700">$50-100</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  <strong>Winner:</strong> Public transit saves 70-85% compared to other options trong major cities
                </p>
              </div>
            </div>
          </section>

          {/* Special Situations */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">✈️ Special Situations</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-purple-50 rounded-lg p-6">
                <h4 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                  <i className="ri-suitcase-line"></i>
                  Traveling with Luggage
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Airport connections:</strong> Most systems accommodate luggage</li>
                  <li>• <strong>Elevators:</strong> Use instead of escalators với heavy bags</li>
                  <li>• <strong>Off-peak travel:</strong> More space, less crowded</li>
                  <li>• <strong>Luggage racks:</strong> Available on some systems</li>
                  <li>• <strong>Alternative:</strong> Consider airport shuttles for heavy luggage</li>
                </ul>
              </div>

              <div className="bg-orange-50 rounded-lg p-6">
                <h4 className="font-semibold text-orange-900 mb-4 flex items-center gap-2">
                  <i className="ri-wheelchair-line"></i>
                  Accessibility
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>ADA compliance:</strong> All new stations wheelchair accessible</li>
                  <li>• <strong>Elevator locations:</strong> Available in station apps</li>
                  <li>• <strong>Priority seating:</strong> Clearly marked areas</li>
                  <li>• <strong>Audio announcements:</strong> For visually impaired</li>
                  <li>• <strong>Paratransit:</strong> Door-to-door service available</li>
                </ul>
              </div>

              <div className="bg-teal-50 rounded-lg p-6">
                <h4 className="font-semibold text-teal-900 mb-4 flex items-center gap-2">
                  <i className="ri-moon-line"></i>
                  Late Night Travel
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Limited service:</strong> Most systems reduce frequency after 10PM</li>
                  <li>• <strong>Safety first:</strong> Stay in well-lit, populated areas</li>
                  <li>• <strong>Night buses:</strong> Often replace rail service</li>
                  <li>• <strong>Plan ahead:</strong> Check last train times</li>
                  <li>• <strong>Alternative transport:</strong> Uber/taxi backup plan</li>
                </ul>
              </div>

              <div className="bg-pink-50 rounded-lg p-6">
                <h4 className="font-semibold text-pink-900 mb-4 flex items-center gap-2">
                  <i className="ri-parent-line"></i>
                  Family Travel
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Stroller policy:</strong> Fold during rush hour on some systems</li>
                  <li>• <strong>Child fares:</strong> Often free under 5, reduced under 12</li>
                  <li>• <strong>Family seating:</strong> Priority areas available</li>
                  <li>• <strong>Entertainment:</strong> Download games for kids</li>
                  <li>• <strong>Snacks:</strong> Keep kids fed during longer rides</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Success Tips */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-6 text-center">🏆 Master Public Transit Like a Local</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-semibold mb-4 text-blue-100">Before You Go:</h3>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• Download city transit apps</li>
                    <li>• Research payment methods</li>
                    <li>• Check service alerts</li>
                    <li>• Plan backup routes</li>
                    <li>• Note hotel transit access</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-4 text-blue-100">During Travel:</h3>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• Allow extra time first few days</li>
                    <li>• Ask locals for help - they're usually friendly</li>
                    <li>• Keep backup transportation fund</li>
                    <li>• Take photos of station names</li>
                    <li>• Stay aware of surroundings</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-4 text-blue-100">Pro Level:</h3>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• Learn rush hour patterns</li>
                    <li>• Use express services when available</li>
                    <li>• Explore different neighborhoods</li>
                    <li>• Try late-night food near transit stops</li>
                    <li>• Share tips with other travelers</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-r from-teal-500 to-blue-500 rounded-xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to master American public transit?</h3>
            <p className="mb-6 text-teal-100">
              Chúng tôi sẽ giúp bạn navigate như người bản địa!
            </p>
            <div className="flex flex-col gap-3 max-w-md mx-auto">
              <button className="bg-white text-teal-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer">
                Tư vấn transit strategy
              </button>
              <button className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg text-lg font-semibold border-2 border-white transition-colors whitespace-nowrap cursor-pointer">
                Tải transit apps guide
              </button>
            </div>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
}
